/**
 * Reset Password Page
 *
 * Set a new password after receiving reset email.
 */

import {
  Alert,
  AlertDescription,
  AuthTemplate,
  Button,
  Center,
  Form,
  Input,
  Label,
  Spinner,
  Stack,
} from '@cortex/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  // Check if user has a valid session from the reset link
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession(!!session);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        setError(error.message);
        return;
      }

      // Password updated successfully
      navigate({
        to: '/login',
        search: { message: 'Password updated successfully. Please sign in.' },
      });
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking session
  if (hasSession === null) {
    return (
      <Center className="bg-background min-h-screen">
        <Spinner size="md" />
      </Center>
    );
  }

  // Show error if no valid session
  if (!hasSession) {
    return (
      <AuthTemplate
        title="Invalid or expired link"
        subtitle="This password reset link is invalid or has expired. Please request a new one."
        centered
      >
        <Button asChild>
          <Link to="/forgot-password">Request new link</Link>
        </Button>
      </AuthTemplate>
    );
  }

  return (
    <AuthTemplate
      title="Set new password"
      subtitle="Enter your new password below."
    >
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Form onSubmit={handleSubmit} className="mt-8" gap="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="••••••••"
            />
          </Stack>

          <Stack gap="xs">
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder="••••••••"
            />
          </Stack>
        </Stack>

        <Button type="submit" loading={isLoading} className="w-full">
          Update password
        </Button>
      </Form>
    </AuthTemplate>
  );
}
